import { useApp } from "@/context/AppContext";
import { fmtMoney } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Plus } from "lucide-react";

export function useCompany() {
  const { activeCompany, activeCompanyId } = useApp();
  return { companyId: activeCompanyId, cur: activeCompany?.base_currency || "XOF", company: activeCompany };
}

export function Money({ value, cur = "XOF", className = "" }) {
  return <span className={`tabular ${className}`}>{fmtMoney(value, cur)}</span>;
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

const STATUS = {
  draft: ["Brouillon", "bg-slate-100 text-slate-700"],
  sent: ["Envoyée", "bg-blue-100 text-blue-700"],
  viewed: ["Vue", "bg-indigo-100 text-indigo-700"],
  partial: ["Partielle", "bg-amber-100 text-amber-700"],
  paid: ["Payée", "bg-emerald-100 text-emerald-700"],
  overdue: ["En retard", "bg-rose-100 text-rose-700"],
  cancelled: ["Annulée", "bg-slate-200 text-slate-500"],
  accepted: ["Accepté", "bg-emerald-100 text-emerald-700"],
  posted: ["Comptabilisée", "bg-emerald-100 text-emerald-700"],
};
export function StatusBadge({ status }) {
  const [label, cls] = STATUS[status] || [status, "bg-slate-100 text-slate-700"];
  return <Badge className={`${cls} border-0 font-medium`} data-testid={`status-${status}`}>{label}</Badge>;
}

export function EmptyState({ title, hint }) {
  return (
    <div className="text-center py-16 border border-dashed border-border rounded-md" data-testid="empty-state">
      <p className="font-medium text-foreground">{title}</p>
      {hint && <p className="text-sm text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export function LineItemsEditor({ lines, setLines, products = [] }) {
  const update = (i, k, v) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const add = () => setLines([...lines, { product_id: "", name: "", qty: 1, unit_price: 0, tax_rate: 0 }]);
  const remove = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const onProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    setLines(lines.map((l, idx) => (idx === i ? { ...l, product_id: pid, name: p?.name || l.name, unit_price: p?.sale_price ?? l.unit_price } : l)));
  };
  return (
    <div className="space-y-2" data-testid="line-items-editor">
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <select className="col-span-4 h-9 rounded-md border border-input bg-background px-2 text-sm" value={l.product_id || ""}
            onChange={(e) => (e.target.value ? onProduct(i, e.target.value) : update(i, "product_id", ""))} data-testid={`line-product-${i}`}>
            <option value="">— Article libre —</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Input className="col-span-3 h-9" placeholder="Désignation" value={l.name} onChange={(e) => update(i, "name", e.target.value)} data-testid={`line-name-${i}`} />
          <Input className="col-span-1 h-9" type="number" value={l.qty} onChange={(e) => update(i, "qty", parseFloat(e.target.value) || 0)} data-testid={`line-qty-${i}`} />
          <Input className="col-span-2 h-9" type="number" placeholder="PU" value={l.unit_price} onChange={(e) => update(i, "unit_price", parseFloat(e.target.value) || 0)} data-testid={`line-price-${i}`} />
          <Input className="col-span-1 h-9" type="number" placeholder="TVA%" value={l.tax_rate} onChange={(e) => update(i, "tax_rate", parseFloat(e.target.value) || 0)} data-testid={`line-tax-${i}`} />
          <Button type="button" variant="ghost" size="icon" className="col-span-1 h-9" onClick={() => remove(i)} data-testid={`line-remove-${i}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={add} data-testid="line-add-button"><Plus className="w-4 h-4 mr-1" /> Ajouter une ligne</Button>
    </div>
  );
}

export function computeTotals(lines) {
  let subtotal = 0, tax = 0;
  lines.forEach((l) => { const lt = (l.qty || 0) * (l.unit_price || 0); subtotal += lt; tax += lt * (l.tax_rate || 0) / 100; });
  return { subtotal, tax, total: subtotal + tax };
}
